import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";
import {
  allowedOfferingVisibility,
  allowedPackageVisibility,
  allowedAddOnVisibility,
} from "./constants";
import { createSupabaseClient } from "../../../hooks/useSupabase";

type SettingsCenterAddress = DatabaseRow<"center_addresses">;
type SettingsCenterOpeningHours = DatabaseRow<"center_operating_hours">;
type SettingsCenterConfigs = DatabaseRow<"center_configs">;
type SettingsCenterAddOn = DatabaseRow<"center_add_ons">;
type SettingsCenterOffering = DatabaseRow<"center_offerings"> & {
  visibility: Extract<
    DatabaseRow<"center_offerings">["visibility"],
    (typeof allowedOfferingVisibility)[number]
  >;
  offering: DatabaseRow<"offerings">;
};

type SettingsCenterPackage = DatabaseRow<"center_packages"> & {
  visibility: Extract<
    DatabaseRow<"center_packages">["visibility"],
    (typeof allowedPackageVisibility)[number]
  >;
  center_offerings: DatabaseRow<"center_offerings">[];
};

export type SettingsCenter = DatabaseRow<"centers"> & {
  center_addresses: SettingsCenterAddress[];
  center_operating_hours: SettingsCenterOpeningHours[];
  center_configs: SettingsCenterConfigs[];
  center_offerings: SettingsCenterOffering[];
  center_packages: SettingsCenterPackage[];
  center_add_ons: SettingsCenterAddOn[];
};

const supabaseSettingsCenterRequest = `
*,
center_addresses ( * ),
center_operating_hours ( * ),
center_configs ( * ),
center_packages (
  *,
  center_offerings (
    *,
    offering: offering_id ( * )
  )
),
center_offerings (
  *,
  offering: offering_id ( * )
),
center_add_ons ( * )
`;

const getSettingsCenterData = async (centerId: string) => {
  const supabaseClient = createSupabaseClient();

  const { error, data } = await supabaseClient
    .from("centers")
    .select(supabaseSettingsCenterRequest)
    .eq("id", centerId)
    .limit(1, { foreignTable: "center_configs" })
    .in(
      "center_offerings.visibility" as keyof SettingsCenter,
      allowedOfferingVisibility,
    )
    .is("center_offerings.center_package_id" as keyof SettingsCenter, null)
    .in(
      "center_packages.visibility" as keyof SettingsCenter,
      allowedPackageVisibility,
    )
    .in(
      "center_add_ons.visibility" as keyof SettingsCenter,
      allowedAddOnVisibility,
    )
    .not("center_offerings.offering_id" as keyof SettingsCenter, "is", null)
    .not(
      "center_packages.center_offerings.offering_id" as keyof SettingsCenter,
      "is",
      null,
    )
    .limit(1)
    .single<SettingsCenter>();

  if (error) throw new Error(error.message);

  return data;
};

export default getSettingsCenterData;
